import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../lib/utils";

// Sprint 38 — Contractual Precision: 1.5px border, sharp corners,
// no background tint by default (paper).
const alertVariants = cva(
  "relative w-full rounded-none border-[1.5px] px-4 py-3 text-sm font-public-sans [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-card border-border text-foreground",
        destructive:
          "border-destructive bg-destructive/5 text-destructive [&>svg]:text-destructive dark:bg-transparent",
        success:
          "border-success bg-success/5 text-success [&>svg]:text-success dark:bg-transparent",
        warning:
          "border-amber-700 bg-amber-50 text-amber-900 [&>svg]:text-amber-700 dark:border-amber-400 dark:bg-transparent dark:text-amber-300 dark:[&>svg]:text-amber-400",
        info:
          "border-accent bg-accent/5 text-accent [&>svg]:text-accent dark:bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-bold leading-none tracking-tight uppercase text-xs", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
